import { useMemo } from "react";
import { useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useSetRecoilState } from "recoil";

import { useYupValidationResolver } from "@ryde/redux";
import { errors, Log, RecaptchaEnterpriseActions } from "@ryde/shared";
import {
  apiDataFetchingLandingAtom,
  ClalLoginStepFormFieldsEnum,
  createActiveStepError,
  createMessage,
  IClalLoginLandingModel,
  landingMessagesAtom,
  LandingStepEnum,
  LandingStepErrorsEnum,
  useLandingActions,
  useLandingApiAuth,
  useLandingOtpErrors,
  useLandingRecaptchaToken,
} from "@ryde/utils-landings";

import { useYupLoginLandingClalValidationSchema } from "./parts";

export const useStepLoginClal = () => {
  const { goToStep, setActiveStepErrors } = useLandingActions();
  const { requestPublicOtp } = useLandingApiAuth();
  const { handleErrors } = useLandingOtpErrors();
  const { getRecaptchaToken } = useLandingRecaptchaToken();
  const setServerErrors = useSetRecoilState(landingMessagesAtom);
  const setApiLoading = useSetRecoilState(apiDataFetchingLandingAtom);
  const { yupLoginLandingClalValidationSchema } =
    useYupLoginLandingClalValidationSchema();

  const resolver = useYupValidationResolver(
    yupLoginLandingClalValidationSchema,
    "landing",
  );

  const methods = useForm<IClalLoginLandingModel>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      email: "",
      acceptTerms: false,
      rydeAcceptTerms: false,
    },
    resolver,
  });

  const {
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { isValid },
  } = methods;

  const emailValue = watch(ClalLoginStepFormFieldsEnum.Email);
  const acceptTermsValue = watch(ClalLoginStepFormFieldsEnum.AcceptTerms);
  const rydeAcceptTermsValue = watch(
    ClalLoginStepFormFieldsEnum.rydeAcceptTerms,
  );

  const isContinueCanBeApply = useMemo(() => {
    return isValid && emailValue && acceptTermsValue && rydeAcceptTermsValue;
  }, [isValid, emailValue, acceptTermsValue, rydeAcceptTermsValue]);

  const handleLogin = useEvent(async (): Promise<void> => {
    const isValidAfterTrigger = await trigger();

    if (!isValidAfterTrigger) {
      return;
    }

    setApiLoading(true);
    setServerErrors(createMessage([]));
    const email = getValues(ClalLoginStepFormFieldsEnum.Email);
    const recaptchaToken = await getRecaptchaToken(
      RecaptchaEnterpriseActions.REQUEST_OTP,
    );

    try {
      const requestOtpResponse = await requestPublicOtp({
        allowExistingPassenger: false,
        recaptchaToken,
        email,
      });

      const { success, error } = requestOtpResponse ?? {};

      if (success) {
        setServerErrors(createMessage([]));
        goToStep(LandingStepEnum.VerificationStep, {
          email,
        });
      } else {
        handleErrors({
          error,
          defaultAction(e) {
            setServerErrors(createMessage([errors.auth.errorWhileRequestOTP]));
          },
        });
      }
    } catch (error) {
      Log.warn("Error while requesting OTP", error);
      setActiveStepErrors(
        createActiveStepError(LandingStepErrorsEnum.GeneralError),
      );
      goToStep(LandingStepEnum.ErrorStep);
    } finally {
      setApiLoading(false);
    }
  });

  return { methods, handleLogin, isContinueCanBeApply };
};
